<script setup>
import { useI18n } from "vue-i18n";
const { locale } = useI18n();
import FrontendLayout from "@/Layouts/FrontendLayout.vue";
import { Head, Link, router, useForm, usePage } from "@inertiajs/vue3";

const props = defineProps({
    service: {
        type: Object,
        required: true,
    },
    locations: {
        type: Object,
        required: true,
    },
});

const form = useForm({
    service_id: props.service.id,
    schedule_time: "",
    location_id: "",
    terms: "",
    privacy: "",
    return_policy: "",
});

const submit = () => {
    try {
        form.post(route("checkout.addToCart"), {
            onSuccess: () => {
                form.reset();
            },
        });
    } catch (error) {
        errorToast("Something went wrong!");
    }
};

const schedule_timeErrorClear = () => {
    return form.clearErrors("schedule_time");
};
const location_idErrorClear = () => {
    return form.clearErrors("location_id");
};
const termsErrorClear = () => {
    return form.clearErrors("terms");
};
const privacyErrorClear = () => {
    return form.clearErrors("privacy");
};
const return_policyErrorClear = () => {
    return form.clearErrors("return_policy");
};
</script>

<template>
    <Head :title="service?.title" />
    <FrontendLayout>
        <section class="text-gray-600 body-font">
            <div
                class="container 2xl:px-24 lg:pt-28 pt-20 xl:pb-14 lg:pb-8 mx-auto"
            >
                <p
                    class="text-black text-center font-medium title-font xl:text-[45px] lg:text-[36px] text-xl xl:pt-8 wow animate__animated animate__fadeInLeft"
                >
                    {{ $t("services.service_confirm") }}
                </p>
                <div class="flex flex-wrap xl:pt-20 lg:pt-8 pt-4">
                    <div
                        class="lg:w-3/5 w-full xl:pr-6 lg:pr-4 pr-0 wow animate__animated animate__fadeInLeft"
                    >
                        <div
                            class="rounded-lg p-4 shadow-lg shadow-gray-300 border"
                        >
                            <div
                                class="block w-full xl:h-[410px] lg:h-[320px] h-full"
                            >
                                <VLazyImage
                                    :src="service?.image"
                                    :alt="service?.title"
                                    class="w-full h-full object-center object-cover"
                                />
                            </div>
                            <div class="service-details">
                                <h1
                                    class="title-font xl:text-3xl text-xl font-medium text-black xl:mb-4 mb-1 xl:mt-5 lg:mt-3 mt-2 tracking-wider leading-relaxed"
                                >
                                    {{
                                        locale === "BN"
                                            ? service?.bn_title
                                            : service?.title
                                    }}
                                </h1>
                                <div
                                    class="leading-relaxed text-black xl:text-[17px] lg:text-[15px] text-[14px]"
                                    v-html="
                                        locale === 'BN'
                                            ? service?.bn_content
                                            : service?.content
                                    "
                                ></div>
                            </div>
                        </div>
                    </div>
                    <!-- submit from  -->
                    <div
                        class="lg:w-2/5 w-full wow animate__animated animate__fadeInRight"
                    >
                        <form @submit.prevent="submit">
                            <input
                                hidden
                                type="number"
                                v-model="form.service_id"
                            />
                            <div
                                class="rounded-lg p-4 shadow-lg shadow-gray-300 border mb-4 lg:mt-0 mt-4"
                            >
                                <label
                                    for="name"
                                    class="xl:text-2xl lg:text-xl text-lg font-medium text-black"
                                    >{{ $t("services.schedule_date") }}</label
                                >
                                <input
                                    type="datetime-local"
                                    id="name"
                                    v-model="form.schedule_time"
                                    @change="schedule_timeErrorClear()"
                                    class="w-full border border-gray-300 bg-[#c5e8eb] bg-opacity-[0.3] hover:border-gray-600 focus:border-primary rounded focus:ring-0 text-sm outline-none text-gray-800 lg:py-1 py-0 mt-1 leading-9 transition-colors duration-200 ease-in-out"
                                    placeholder="Schedule date and time"
                                    required
                                />
                                <p
                                    class="text-red-600 dark:text-red-300 xl:mb-6 lg:mb-4 mb-2"
                                >
                                    {{ form.errors.schedule_time }}
                                </p>
                            </div>
                            <div
                                class="rounded-lg p-4 shadow-lg shadow-gray-300 border mb-4"
                            >
                                <label
                                    for="location"
                                    class="xl:text-2xl lg:text-xl text-lg font-medium text-black"
                                    >{{ $t("services.location") }}</label
                                >
                                <select
                                    id="location"
                                    v-model="form.location_id"
                                    @change="location_idErrorClear()"
                                    class="w-full border lg:text-lg text-base border-gray-300 bg-[#c5e8eb] bg-opacity-[0.3] hover:border-gray-600 focus:border-primary rounded focus:ring-0 leading-relaxed outline-none text-gray-800 lg:py-2 py-1 mt-1 transition-colors duration-200 ease-in-out"
                                    required
                                >
                                    <option value="">Choose Location</option>
                                    <option
                                        :value="location?.id"
                                        v-for="location in locations"
                                        :key="location.id"
                                    >
                                        {{ location?.name }}
                                    </option>
                                </select>
                                <p
                                    class="text-red-600 dark:text-red-300 xl:mb-6 lg:mb-4 mb-2"
                                >
                                    {{ form.errors.location_id }}
                                </p>
                            </div>
                            <div
                                class="rounded-lg px-4 lg:py-6 py-4 shadow-lg shadow-gray-300 border xl:mb-8 mb-4"
                            >
                                <div class="xl:mb-3 mb-2">
                                    <input
                                        id=""
                                        type="checkbox"
                                        value=""
                                        v-model="form.terms"
                                        @change="termsErrorClear()"
                                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                        required
                                    />
                                    <label
                                        for="checkbox-1"
                                        class="xl:ml-6 lg:ml-4 ml-2 xl:text-[16px] lg:text-[14px] text-[13px] font-medium text-gray-900 dark:text-gray-300"
                                        >{{ $t("services.agree") }}
                                        <Link
                                            :href="
                                                route('policy.page', 'terms')
                                            "
                                            class="text-blue-600 underline dark:text-blue-500"
                                            >{{ $t("nav.terms") }}
                                        </Link>
                                    </label>
                                    <p
                                        class="text-red-600 dark:text-red-300 xl:mb-6 lg:mb-4 mb-2"
                                    >
                                        {{ form.errors.terms }}
                                    </p>
                                </div>

                                <div class="xl:mb-3 mb-2">
                                    <input
                                        id=""
                                        type="checkbox"
                                        value=""
                                        v-model="form.privacy"
                                        @change="privacyErrorClear()"
                                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                        required
                                    />
                                    <label
                                        for="checkbox-1"
                                        class="xl:ml-6 lg:ml-4 ml-2 xl:text-[16px] lg:text-[14px] text-[13px] font-medium text-gray-900 dark:text-gray-300"
                                        >{{ $t("services.agree") }}
                                        <Link
                                            :href="
                                                route(
                                                    'policy.page',
                                                    'privacy-policy'
                                                )
                                            "
                                            class="text-blue-600 underline dark:text-blue-500"
                                            >{{ $t("nav.privacy") }}
                                        </Link>
                                    </label>
                                    <p
                                        class="text-red-600 dark:text-red-300 xl:mb-6 lg:mb-4 mb-2"
                                    >
                                        {{ form.errors.privacy }}
                                    </p>
                                </div>
                                <div>
                                    <input
                                        id=""
                                        type="checkbox"
                                        value=""
                                        v-model="form.return_policy"
                                        @change="return_policyErrorClear()"
                                        class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                        required
                                    />
                                    <label
                                        for="checkbox-1"
                                        class="xl:ml-6 lg:ml-4 ml-2 xl:text-[16px] lg:text-[14px] text-[13px] font-medium text-gray-900 dark:text-gray-300"
                                        >{{ $t("services.agree") }}
                                        <Link
                                            :href="
                                                route('policy.page', 'refund')
                                            "
                                            class="text-blue-600 underline dark:text-blue-500"
                                            >{{ $t("nav.return_policy") }}</Link
                                        >
                                    </label>
                                    <p
                                        class="text-red-600 dark:text-red-300 xl:mb-6 lg:mb-4 mb-2"
                                    >
                                        {{ form.errors.return_policy }}
                                    </p>
                                </div>
                            </div>

                            <button
                                type="submit"
                                class="flex w-full text-center justify-center items-center text-white bg-primary border-0 lg:py-2 py-1 px-8 focus:outline-none hover:bg-[#1565C0] transition duration-300 rounded xl:text-lg lg:text-base text-sm uppercase"
                            >
                                {{ $t("contact.get_started") }}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    </FrontendLayout>
</template>

<style scoped></style>
